import { inject } from '@angular/core';
import { PageFilterGroup } from '@common/paged-data/types/page-filter-group.type';
import { PageSort } from '@common/paged-data/types/page-sort.type';
import { Page } from '@common/paged-data/types/page.type';
import { SortDirection } from '@common/paged-data/types/sort-direction.type';
import { distinctEquivalenceUntilChanged } from '@common/util/distinct-equivalence-until-changed.operator';
import { Store } from '@ngxs/store';
import { DataList } from '@portal-core/data/collection/models/data-list.model';
import { GridPage } from '@portal-core/data/collection/models/grid-state.model';
import { CollectionStateBase } from '@portal-core/data/collection/services/collection-state.base';
import { CollectionPredicate, CollectionPropertyPairs } from '@portal-core/data/collection/types/collection-property-args.type';
import { AddDataListPage, AddGridPage, AddItems, AddItemsToListById, ClearGrid, ClearGridFilters, ClearLists, ClearPagedDataList, DeleteItems, RemoveItems, RemovePagedDataListItems, Reset, ResetGrid, ResetPagedDataList, SetGridFilterByName, SetGridOrder, SetGridPageSize, SetGridVisibleColumns, SetListById, SetPagedDataListFilterByName, SetPagedDataListOrder } from '@portal-core/data/collection/types/collection-state.type';
import { McModel } from '@portal-core/data/common/models/mc-model.model';
import { ModelId, ModelIds } from '@portal-core/data/common/types/mc-model.type';
import { filter, find, get, keyBy } from 'lodash';
import { Observable, Subject, distinctUntilChanged, map } from 'rxjs';

export abstract class CollectionDataServiceBase<T extends McModel> {
  static idProperty: string = 'Id';
  updatingItems$: Subject<Dictionary | Array<T>> = new Subject<Dictionary | Array<T>>();

  protected readonly store = inject(Store);
  protected abstract stateType: typeof CollectionStateBase;

  addDefaultItems$(): Observable<void> {
    // defaultItems is declared as McModel[] on the non-generic state base; assert it to this collection's item shape.
    return this.addItems$(this.stateType.defaultItems as Partial<T>[]);
  }

  reset$(): Observable<void> {
    return this.store.dispatch(new Reset(this.stateType.source));
  }

  // Items
  getItems<S extends T = T>(): Dictionary<S> {
    return this.store.selectSnapshot(this.stateType.itemsSelector);
  }

  getItems$<S extends T = T>(): Observable<Dictionary<S>> {
    return this.store.select(this.stateType.itemsSelector);
  }

  addItems$<S extends T = T>(items: Dictionary<Partial<S>> | Partial<S>[]): Observable<void> {
    if (Array.isArray(items)) {
      items = keyBy(items, CollectionDataServiceBase.idProperty);
    }
    this.updatingItems$.next(items);
    return this.store.dispatch(new AddItems(this.stateType.source, { items }));
  }

  // Simply an alias for addItem$ but with better semantic meaning when updating items instead of adding them
  updateItems$<S extends T = T>(items: Dictionary<Partial<S>> | Partial<S>[]): Observable<void> {
    return this.addItems$(items);
  }

  /**
   * Dispatches {@link RemoveItems} to evict the given items from the cache only — they still exist on
   * the server, so grid and paged-list totals are left untouched. Use {@link deleteItems$} for a delete.
   * @param items the items (or their ids) to evict from the cache.
   */
  removeItems$(items: Dictionary<T> | T[] | ModelId[]): Observable<void> {
    const itemIds = this.mapItemsToIds(items);
    return this.store.dispatch(new RemoveItems(this.stateType.source, { itemIds }));
  }

  /**
   * Dispatches {@link DeleteItems} for items that have been permanently deleted: they are dropped from
   * the cache and stripped from every grid and paged data list, whose totals are decremented so
   * paginators stay accurate. Use {@link removeItems$} for a plain cache eviction.
   * @param items the items (or their ids) that were deleted.
   */
  deleteItems$(items: Dictionary<T> | T[] | ModelId[]): Observable<void> {
    const itemIds = this.mapItemsToIds(items);
    return this.store.dispatch(new DeleteItems(this.stateType.source, { itemIds }));
  }

  // Item by id
  getItemById<S extends T = T>(itemId: ModelId): S {
    const items = this.store.selectSnapshot(this.stateType.itemsSelector);
    return items ? items[itemId] : undefined;
  }

  getItemById$<S extends T = T>(itemId: ModelId): Observable<S> {
    return this.store.select(this.stateType.itemByIdSelector).pipe(
      map(fn => fn(itemId) as S)
    );
  }

  // Lists
  getListById(listName: string, listId: ModelId): DataList {
    const lists = this.store.selectSnapshot(this.stateType.listsSelector);
    return lists && lists[listName] ? lists[listName][listId] : undefined;
  }

  getListById$(listName: string, listId: ModelId): Observable<DataList> {
    return this.store.select(this.stateType.listByIdSelector).pipe(
      map(fn => fn(listName, listId)),
    );
  }

  setListById$(listName: string, listId: ModelId, items: T[]): Observable<void> {
    this.updatingItems$.next(items);
    return this.store.dispatch(new SetListById(this.stateType.source, { items, listId, listName }));
  }

  addItemsToListById$(listName: string, listId: ModelId, items: T[]): Observable<void> {
    this.updatingItems$.next(items);
    return this.store.dispatch(new AddItemsToListById(this.stateType.source, { items, listId, listName }));
  }

  clearLists$(): Observable<void> {
    return this.store.dispatch(new ClearLists(this.stateType.source));
  }

  /*
   * Paged Data Lists
   */
  getPagedDataListItems(pagedDataListId: string): T[] {
    return this.store.selectSnapshot(this.stateType.pagedDataListItems)(pagedDataListId) as T[];
  }

  getPagedDataListItems$(pagedDataListId: string): Observable<T[]> {
    return this.store.select(this.stateType.pagedDataListItems).pipe(
      map(fn => fn(pagedDataListId) as T[]),
      distinctUntilChanged()
    );
  }

  getPagedDataListPageIndex(pagedDataListId: string): number {
    return this.store.selectSnapshot(this.stateType.pagedDataListPageIndex)(pagedDataListId);
  }

  getPagedDataListTotal(pagedDataListId: string): number {
    return this.store.selectSnapshot(this.stateType.pagedDataListTotal)(pagedDataListId);
  }

  getPagedDataListLength(pagedDataListId: string): number {
    return this.store.selectSnapshot(this.stateType.pagedDataListLength)(pagedDataListId);
  }

  getPagedDataListAllPagesLoaded(pagedDataListId: string): boolean {
    return this.store.selectSnapshot(this.stateType.pagedDataListAllPagesLoaded)(pagedDataListId);
  }

  getPagedDataListAllPagesLoaded$(pagedDataListId: string): Observable<boolean> {
    return this.store.select(this.stateType.pagedDataListAllPagesLoaded).pipe(
      map(fn => fn(pagedDataListId)),
      distinctUntilChanged()
    );
  }

  // Filters
  getPagedDataListFilters(pagedDataListId: string): Dictionary<PageFilterGroup> {
    return this.store.selectSnapshot(this.stateType.pagedDataListFilters)(pagedDataListId);
  }

  getPagedDataListFilters$(pagedDataListId: string): Observable<Dictionary<PageFilterGroup>> {
    return this.store.select(this.stateType.pagedDataListFilters).pipe(
      map(fn => fn(pagedDataListId)),
      distinctEquivalenceUntilChanged()
    );
  }

  // Filter by Name
  getPagedDataListFilterByName(pagedDataListId: string, name: string): PageFilterGroup {
    return this.store.selectSnapshot(this.stateType.pagedDataListFilterByName)(pagedDataListId, name);
  }

  getPagedDataListFilterByName$(pagedDataListId: string, name: string): Observable<PageFilterGroup> {
    return this.store.select(this.stateType.pagedDataListFilterByName).pipe(
      map(fn => fn(pagedDataListId, name)),
      distinctEquivalenceUntilChanged()
    );
  }

  setPagedDataListFilterByName$(dataListId: string, name: string, value: PageFilterGroup): Observable<void> {
    return this.store.dispatch(new SetPagedDataListFilterByName(this.stateType.source, { dataListId, name, value }));
  }

  // Order
  getPagedDataListOrder(pagedDataListId: string): PageSort {
    return this.store.selectSnapshot(this.stateType.pagedDataListOrder)(pagedDataListId);
  }

  getPagedDataListOrder$(pagedDataListId: string): Observable<PageSort> {
    return this.store.select(this.stateType.pagedDataListOrder).pipe(
      map(fn => fn(pagedDataListId)),
      distinctEquivalenceUntilChanged()
    );
  }

  setPagedDataListOrder$(dataListId: string, orderBy: string, orderDirection: SortDirection): Observable<void> {
    return this.store.dispatch(new SetPagedDataListOrder(this.stateType.source, {
      dataListId,
      order: {
        by: orderBy,
        dir: orderDirection
      }
    }));
  }

  // Pages
  addDataListPage$(dataListId: string, page: Page<T>): Observable<void> {
    this.updatingItems$.next(page.Items);
    return this.store.dispatch(new AddDataListPage(this.stateType.source, { dataListId, page }));
  }

  removePagedDataListItems$(dataListId: string, itemIds: ModelIds): Observable<void> {
    return this.store.dispatch(new RemovePagedDataListItems(this.stateType.source, { dataListId, itemIds }));
  }

  resetPagedDataList$(dataListId: string): Observable<void> {
    return this.store.dispatch(new ResetPagedDataList(this.stateType.source, { dataListId }));
  }

  clearPagedDataList$(dataListId: string): Observable<void> {
    return this.store.dispatch(new ClearPagedDataList(this.stateType.source, { dataListId }));
  }

  /*
   * Grids
   */

  /**
   * Returns an observable for a page's items in a grid.
   * @param gridId The id of the grid to get the items for.
   * @param pageIndex The page index to get the items for.
   */
  getGridItems$(gridId: string, pageIndex: number): Observable<T[]> {
    return this.store.select(this.stateType.gridItems).pipe(
      map(fn => fn(gridId, pageIndex) as T[]),
      distinctUntilChanged()
    );
  }

  /**
   * Adds a page to a grid.
   * @param gridId The id of the grid to add the page to.
   * @param page The page to be added to the grid.
   */
  addGridPage$(gridId: string, page: Page<T>, pageSize: number): Observable<void> {
    this.updatingItems$.next(page.Items);
    return this.store.dispatch(new AddGridPage(this.stateType.source, { gridId, page, pageSize }));
  }

  /**
   * Returns a GridPage from a grid.
   * @param gridId The id of the grid to add the page to.
   * @param pageIndex The index of the page to get.
   */
  getGridPage(gridId: string, pageIndex: number): GridPage {
    return this.store.selectSnapshot(this.stateType.gridPage)(gridId, pageIndex);
  }

  /**
   * Returns an observable of a GridPage from a grid.
   * @param gridId The id of the grid to add the page to.
   * @param pageIndex The index of the page to get.
   */
  getGridPage$(gridId: string, pageIndex: number): Observable<GridPage> {
    return this.store.select(this.stateType.gridPage).pipe(
      map(fn => fn(gridId, pageIndex)),
      distinctUntilChanged()
    );
  }

  /**
   * Removes all the data and config associated with the grid.
   * @param gridId The id of the grid.
   */
  resetGrid$(gridId: string): Observable<void> {
    return this.store.dispatch(new ResetGrid(this.stateType.source, { gridId }));
  }

  /**
   * Removes the pages from the grid.
   * @param gridId The id of the grid.
   */
  clearGrid$(gridId: string): Observable<void> {
    return this.store.dispatch(new ClearGrid(this.stateType.source, { gridId }));
  }

  /**
   * Returns the cursor for page in a grid.
   * @param gridId The id of the grid to get the cursor for.
   * @param pageIndex The page index to get the cursor for.
   */
  getGridCursor(gridId: string, pageIndex: number): number {
    return this.store.selectSnapshot(this.stateType.gridCursor)(gridId, pageIndex);
  }

  /**
   * Returns the total number of items across every page of the grid.
   * @param gridId The id of the grid.
   */
  getGridTotal(gridId: string): number {
    return this.store.selectSnapshot(this.stateType.gridTotal)(gridId);
  }

  /**
   * Returns an observable of the total number of items across every page of the grid.
   * @param gridId The id of the grid.
   */
  getGridTotal$(gridId: string): Observable<number> {
    return this.store.select(this.stateType.gridTotal).pipe(
      map(fn => fn(gridId)),
      distinctUntilChanged()
    );
  }

  /**
   * Returns the limited total number of items across every page of the grid.
   * The server will sometimes return a limited total number of items for performance reasons.
   * @param gridId The id of the grid.
   */
  getGridLimitedTotal(gridId: string): number {
    return this.store.selectSnapshot(this.stateType.gridLimitedTotal)(gridId);
  }

  /**
   * Returns an observable of the limited total number of items across every page of the grid.
   * The server will sometimes return a limited total number of items for performance reasons.
   * @param gridId The id of the grid.
   */
  getGridLimitedTotal$(gridId: string): Observable<number> {
    return this.store.select(this.stateType.gridLimitedTotal).pipe(
      map(fn => fn(gridId)),
      distinctUntilChanged()
    );
  }

  /**
   * Returns the filters on the grid.
   * @param gridId The id of the grid.
   */
  getGridFilters(gridId: string): Dictionary<PageFilterGroup> {
    return this.store.selectSnapshot(this.stateType.gridFilters)(gridId);
  }

  /**
   * Returns an observable of the filters on the grid.
   * @param gridId The id of the grid.
   */
  getGridFilters$(gridId: string): Observable<Dictionary<PageFilterGroup>> {
    return this.store.select(this.stateType.gridFilters).pipe(
      map(fn => fn(gridId)),
      distinctEquivalenceUntilChanged()
    );
  }

  /**
   * Returns the filter on the grid with the given name.
   * @param gridId The id of the grid.
   * @param name: The name of the filter.
   */
  getGridFilterByName(gridId: string, name: string): PageFilterGroup {
    return this.store.selectSnapshot(this.stateType.gridFilterByName)(gridId, name);
  }

  /**
   * Returns an observable of the filter on the grid with the given name.
   * @param gridId The id of the grid.
   * @param name: The name of the filter.
   */
  getGridFilterByName$(gridId: string, name: string): Observable<PageFilterGroup> {
    return this.store.select(this.stateType.gridFilterByName).pipe(
      map(fn => fn(gridId, name)),
      distinctEquivalenceUntilChanged()
    );
  }

  /**
   * Sets the filter on the grid with the given name.
   * @param gridId The id of the grid.
   * @param name: The name of the filter.
   * @param value: The value of the filter.
   */
  setGridFilterByName$(gridId: string, name: string, value: PageFilterGroup): Observable<void> {
    return this.store.dispatch(new SetGridFilterByName(this.stateType.source, { gridId, name, value }));
  }

  /**
   * Clear all the filters on the grid.
   * @param gridId The id of the grid.
   */
  clearGridFilters$(gridId: string): Observable<void> {
    return this.store.dispatch(new ClearGridFilters(this.stateType.source, { gridId }));
  }

  /**
   * Returns the page size of the grid.
   * @param gridId The id of the grid.
   */
  getGridPageSize(gridId: string): number {
    return this.store.selectSnapshot(this.stateType.gridPageSize)(gridId);
  }

  /**
   * Returns an observable of the page size of the grid.
   * @param gridId The id of the grid.
   */
  getGridPageSize$(gridId: string): Observable<number> {
    return this.store.select(this.stateType.gridPageSize).pipe(
      map(fn => fn(gridId)),
      distinctUntilChanged()
    );
  }

  /**
   * Sets the page size of the grid.
   * @param gridId The id of the grid.
   * @param pageSize The new page size of the grid.
   */
  setGridPageSize$(gridId: string, pageSize: number): Observable<void> {
    return this.store.dispatch(new SetGridPageSize(this.stateType.source, { gridId, pageSize }));
  }

  /**
   * Returns the sort order on the grid.
   * @param gridId The id of the grid.
   */
  getGridOrder(gridId: string): PageSort {
    return this.store.selectSnapshot(this.stateType.gridOrder)(gridId);
  }

  /**
   * Returns an observable of the sort order on the grid.
   * @param gridId The id of the grid.
   */
  getGridOrder$(gridId: string): Observable<PageSort> {
    return this.store.select(this.stateType.gridOrder).pipe(
      map(fn => fn(gridId)),
      distinctEquivalenceUntilChanged()
    );
  }

  /**
   * Sets the sort order of the grid.
   * @param gridId The id of the grid.
   * @param orderBy The name of the column to order the grid by.
   * @param orderDirection The direction to sort the column by.
   */
  setGridOrder$(gridId: string, orderBy: string, orderDirection: SortDirection): Observable<void> {
    return this.store.dispatch(new SetGridOrder(this.stateType.source, {
      gridId,
      order: {
        by: orderBy,
        dir: orderDirection
      }
    }));
  }

  /**
   * Returns the visible columns for the grid.
   * @param gridId The id of the grid.
   */
  getGridVisibleColumns(gridId: string): string[] {
    return this.store.selectSnapshot(this.stateType.gridVisibleColumns)(gridId);
  }

  /**
   * Returns an observable of the visible columns for the grid.
   * @param gridId The id of the grid.
   */
  getGridVisibleColumns$(gridId: string): Observable<string[]> {
    return this.store.select(this.stateType.gridVisibleColumns).pipe(
      map(fn => fn(gridId)),
      distinctEquivalenceUntilChanged()
    );
  }

  /**
   * Sets the visible columns for the grid.
   * @param gridId The id of the grid.
   * @param visibleColumns An array of column names to be displayed in the grid. The order of the names is used as the order of the columns in the grid.
   */
  setGridVisibleColumns$(gridId: string, visibleColumns: string[]): Observable<void> {
    return this.store.dispatch(new SetGridVisibleColumns(this.stateType.source, { gridId, visibleColumns }));
  }

  /**
   * Returns the first cached item that matches every one of the given `(property-path, value)` pairs,
   * or `undefined` if none match. The arguments are a flat, repeating list — a property path followed
   * by the value it must equal, repeated — and are type-checked against the item type: each path must
   * be a property of the item (use a dotted path to reach a nested property, e.g. `'User.Id'`), and
   * each value must match the type at that path. Values are compared by strict equality.
   *
   * This reads a one-time snapshot of the current state. Use {@link getItemByProperties$} for a value
   * that re-emits as the collection changes, or {@link getItemByPredicate} to match on an arbitrary
   * condition instead of property equality.
   *
   * @example
   * // the workspace file for this project + branch + path
   * this.workspaceFilesDataService.getItemByProperties('projectId', projectId, 'branchName', branchName, 'filePath', filePath);
   */
  getItemByProperties<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): T {
    return find(this.store.selectSnapshot(this.stateType.itemsSelector), item => argsMatch(item, args));
  }

  /**
   * Observable form of {@link getItemByProperties}: emits the first item matching every given
   * `(property-path, value)` pair, and re-emits whenever the collection changes (emitting `undefined`
   * when nothing matches). The paired arguments are typed exactly as in {@link getItemByProperties}.
   *
   * @example
   * this.reviewFilesService.getItemByProperties$('ReviewPackageId', reviewPackageId, 'Path', path);
   */
  getItemByProperties$<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): Observable<T> {
    return this.store.select(this.stateType.itemByPropertiesSelector).pipe(
      map(fn => fn(...args) as T)
    );
  }

  /**
   * Returns every cached item that matches all of the given `(property-path, value)` pairs (an empty
   * array when none match). Takes the same paired, type-checked arguments as
   * {@link getItemByProperties}, but returns all matches rather than only the first.
   *
   * @example
   * this.workspaceFilesDataService.getItemsByProperties('projectId', projectId, 'branchName', branchName);
   */
  getItemsByProperties<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): T[] {
    return filter(this.store.selectSnapshot(this.stateType.itemsSelector), item => argsMatch(item, args));
  }

  /**
   * Observable form of {@link getItemsByProperties}: emits the array of all items matching the given
   * `(property-path, value)` pairs, and re-emits whenever the collection changes.
   */
  getItemsByProperties$<A extends readonly unknown[]>(...args: A & CollectionPropertyPairs<T, A>): Observable<T[]> {
    return this.store.select(this.stateType.itemsByPropertiesSelector).pipe(
      map(fn => fn(...args) as T[])
    );
  }

  /**
   * Returns the first cached item for which `predicate` returns `true`, or `undefined` if none do.
   * Use this to match on an arbitrary condition; for plain property equality prefer
   * {@link getItemByProperties}, which type-checks the property names and their values.
   *
   * @example
   * this.branchesDataService.getItemByPredicate(branch => branch.Name === name && !branch.Default);
   */
  getItemByPredicate(predicate: CollectionPredicate<T>): T {
    return find(this.store.selectSnapshot(this.stateType.itemsSelector), item => predicate(item));
  }

  private mapItemsToIds(items: Dictionary<T> | T[] | ModelId[]): ModelId[] {
    return Object.values(items).map((item: T | ModelId) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return item;
      } else {
        return item.Id;
      }
    });
  }
}

/**
 * Returns whether `item` satisfies every `(property-path, value)` pair in a flat argument list: each
 * even element is read from `item` with lodash `get` and compared with `!==` to the value after it.
 * Used by the collection by-properties query methods, whose public signatures
 * ({@link CollectionPropertyPairs}) type the arguments — here they are dereferenced dynamically.
 */
function argsMatch(item: unknown, args: readonly unknown[]): boolean {
  for (let i = 0; i < args.length; i += 2) {
    if (get(item, args[i] as string) !== args[i + 1]) {
      return false;
    }
  }

  return true;
}
